import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FlightSearchStore } from '@calendar/flight-search/flight-search.store';
import { SavedFlight } from '@calendar/flight-search/models/saved-flight';
import { LegView } from '@sequence/models/view/leg.view';
import { SequenceView } from '@sequence/models/view/sequence.view';
import { SequenceService } from '@sequence/sequence.service';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';

interface FlightDate {
  date: string;
  flights: SavedFlight[];
}

interface GroupedFlights {
  groups: FlightDate[];
}

@Component({
  selector: 'app-saved-flights',
  templateUrl: './saved-flights.component.html',
  styleUrl: './saved-flights.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedFlightsComponent implements OnInit {
  savedFlights$ = this.getSavedFlights();

  constructor(
    private flightSearchStore: FlightSearchStore,
    private sequenceService: SequenceService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Filter out flights that departed earlier than yesterday.
    this.flightSearchStore.removeExpiredFlights();
  }

  getSavedFlights(): Observable<GroupedFlights> {
    return this.flightSearchStore.savedFlights$.pipe(
      map((savedFlights) => {
        // Populate the leg observable.
        // Gets sequence, then finds matching leg.
        for (const savedFlight of savedFlights) {
          if (savedFlight.seqRequest) {
            savedFlight.leg$ = this.sequenceService
              .getSequence(savedFlight.seqRequest)
              .pipe(
                map((sequence) => {
                  if (sequence.response) {
                    const seqView = new SequenceView(sequence.response);
                    // Find matching flight.
                    for (const dp of seqView.dutyPeriods) {
                      for (const leg of dp.legs) {
                        if (
                          parseInt(leg.flightNumber) ===
                            parseInt(savedFlight.flightNumber) &&
                          leg.depStation === savedFlight.depStation &&
                          leg.depTimeSched?.toISODate() ===
                            DateTime.fromISO(savedFlight.date).toISODate()
                        ) {
                          return sequence.into<LegView>(leg);
                        }
                      }
                    }
                  }
                  return sequence.into<LegView>();
                }),
              );
          }
        }
        return this.groupByDate(savedFlights);
      }),
    );
  }

  groupByDate(savedFlights: SavedFlight[]): GroupedFlights {
    const groupedFlights: GroupedFlights = { groups: [] };
    const dateSortedFlights = savedFlights.sort((a, b) => {
      const dateA = DateTime.fromISO(a.date).set({
        hour: parseInt(a.depTime.substring(0, 2)),
        minute: parseInt(a.depTime.substring(2)),
      });
      const dateB = DateTime.fromISO(b.date).set({
        hour: parseInt(b.depTime.substring(0, 2)),
        minute: parseInt(b.depTime.substring(2)),
      });
      return dateA.toMillis() - dateB.toMillis();
    });

    for (const flight of dateSortedFlights) {
      let found = false;

      for (const group of groupedFlights.groups) {
        if (group.date === flight.date) {
          group.flights.push(flight);
          found = true;
        }
      }

      if (!found) {
        groupedFlights.groups.push({
          date: flight.date,
          flights: [flight],
        });
      }
    }

    return groupedFlights;
  }

  /** Take ISO string and reformat for display. */
  formatDate(date: string): string {
    return DateTime.fromISO(date).toFormat('LLL dd');
  }

  onFlightClick(flight: SavedFlight): void {
    // If flightUrl was stored, navigate to it. Otherwise navigate to lookup.
    if (flight.flightUrl) {
      this.router.navigate([flight.flightUrl]);
    } else {
      this.router.navigate([
        'lookup-sequence',
        flight.flightNumber,
        flight.depStation,
        flight.arrStation,
        flight.date,
      ]);
    }
  }

  onDeleteClick(flightId: string): void {
    this.flightSearchStore.removeSavedFlight(flightId);
  }
}
